import { direction } from 'direction';
import { fromEvent, merge, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { ContentEditableElement } from '../../editors/base/contentEditableEditor';
import { isListElement, ListElement } from '../../helpers/dom/list';
import { findAncestor } from '../../helpers/dom/node';
import { isParagraphElement } from '../../helpers/dom/paragraph';
import { computeIsElementEmpty } from '../../helpers/elementHtmlEditor/empty';
import { ElementSelectionService } from './elementSelectionService';
import { createElementEffectService } from './elementService';

/** Sets the text direction for the current paragraph. */
export function createElementTextDirectionService(
  element$: Observable<ContentEditableElement | null>,
  selectionService: ElementSelectionService,
  hasTextDirectionService: boolean,
) {
  return createElementEffectService(element$, (element, document) => {
    return merge(
      applyTextDirectionAsYouTypeEffect(element, document, selectionService, hasTextDirectionService),
      applyTextDirectionOnCopyPasteEffect(element, selectionService, hasTextDirectionService),
    );
  });
}

function applyTextDirectionAsYouTypeEffect(
  element: Element,
  document: Document,
  selectionService: ElementSelectionService,
  hasTextDirectionService: boolean,
) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    tap(event => {
      if (!hasTextDirectionService) return;

      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const { selection } = selectionService;
      if (!selection) {
        return;
      }

      const currentListElement = findAncestor(
        selection.anchorNode,
        (node: Node): node is ListElement => isTopLevelNode(node, element) && isListElement(node),
        element,
      );

      if (currentListElement) {
        const { textContent } = currentListElement;
        if (!textContent || textContent.length < 1) {
          // This is the first character typed in a list, use it to set the direction of the element
          const { key } = event;
          setElementDir(currentListElement, direction(key));
        }
        return;
      }

      const currentParagraph = findAncestor(
        selection.anchorNode,
        (node: Node): node is HTMLDivElement => isTopLevelNode(node, element) && isParagraphElement(node),
        element,
      );

      if (!currentParagraph) {
        return;
      }

      // We set the text direction based only on the first character typed
      if (computeIsElementEmpty(document, currentParagraph)) {
        const { key } = event;
        setElementDir(currentParagraph, direction(key));
      }
    }),
    share(),
  );
}

function applyTextDirectionOnCopyPasteEffect(
  element: Element,
  selectionService: ElementSelectionService,
  hasTextDirectionService: boolean,
) {
  return fromEvent<ClipboardEvent>(element, 'paste').pipe(
    tap(event => {
      if (!hasTextDirectionService) return;

      const { selection } = selectionService;
      if (!selection) {
        return;
      }

      const currentParagraph = findAncestor(
        selection.anchorNode,
        (node: Node): node is HTMLDivElement => isTopLevelNode(node, element) && isParagraphElement(node),
        element,
      );

      if (!currentParagraph) {
        return;
      }

      const { clipboardData } = event;
      if (!clipboardData) {
        return;
      }

      const currentParagraphTextLength = currentParagraph.textContent?.length;
      const pastedText = clipboardData.getData('text/plain');
      const pastedTextLength = pastedText.length;

      // If the pasted text is all of the current paragraph, set the text direction.
      if (currentParagraphTextLength === pastedTextLength) {
        setElementDir(currentParagraph, direction(pastedText));
      }
    }),
    share(),
  );
}

function setElementDir(element: HTMLDivElement | ListElement, dir: 'ltr' | 'rtl' | 'neutral') {
  // Since ltr is the default, avoid adding the explicit styling here.
  if (dir === 'ltr' && element.dir !== 'rtl') return;

  // If the returned direction is neutral, just ignore it.
  if (dir !== 'neutral') {
    // eslint-disable-next-line no-param-reassign
    element.dir = dir;
  }
}

/**
 * We only want to set the text direction on the nodes nested at the first level
 * within out content-editable element. This avoids lots of funky bugs around nested
 * lists and whatnot.
 *
 * @param node
 * @param container
 * @returns True if the container is the parentNode of node.
 */
function isTopLevelNode(node: Node, container: Node): boolean {
  return node.parentNode === container;
}
